// P5 (ADR "Fatia 4 destravada") — PROPAGAÇÃO DE ACESSO, metade CLIENTE.
//
// Vive SÓ em web-comite (zona R-2.6 autorizada): um membro que JÁ LÊ o caso (tem
// a DEK em claro via withCaseDek) re-embrulha a DEK (RSA-OAEP) para os membros que
// entraram DEPOIS do submit e ficaram cegos. Só ciphertext opaco sobe ao servidor;
// a DEK NUNCA sai do browser. Espelha o backfill de wrap-no-submit (Fatia 2).
//
// Duas funções, separadas para testabilidade:
//   - findMissingMembers: PURO (sem cripto) — compara /public-keys × wraps do caso.
//   - rewrapForMissingMembers: cripto — gera os wraps RSA a partir da DEK em escopo.
// A busca de /public-keys e o POST vivem no CaseDetailPage (I/O autenticado).

import { wrapDekForMember } from '@aions/crypto';
import { IA_ANALISTA_MEMBER_ID } from '@aions/core';
import { bytesToBase64 } from './bytes';

/** Entrada de /api/denuncia/public-keys — {memberId, publicKeyJwk} de membro ativo com keypair. */
export interface PublicKeyEntry {
  memberId: string;
  publicKeyJwk: string;
  /** Referência OPACA da keypair vigente do membro (impressão digital da pública ou
   *  contador de geração — o filtro não depende de qual). Ausente ⇒ keypair de
   *  origem, antes da maquinaria de geração: `REF_ORIGEM`, para o acervo atual
   *  continuar valendo sem migração. */
  keypairRef?: string;
}

/** Wraps e públicas sem `keypairRef` são anteriores à maquinaria de geração e contam
 *  como a MESMA referência — back-compat: nada no acervo de hoje vira obsoleto. */
export const REF_ORIGEM = 'origem';

function refDe(x: { keypairRef?: string } | undefined): string {
  return x?.keypairRef ?? REF_ORIGEM;
}

/** Wrap pronto pro wire — {memberId, wrappedDek} base64 (RSA-OAEP-2048). */
export interface MemberWrap {
  memberId: string;
  wrappedDek: string;
  /** Referência da keypair para a qual ESTE wrap foi feito. É o que permite
   *  reconhecer wrap morto sem apagar nada do caso. */
  keypairRef?: string;
}

/**
 * PURO — membros ATIVOS COM keypair (de /public-keys) que ainda NÃO CONSEGUEM LER o
 * caso. Exclui a IA-Analista (humanos nesta entrega; a IA tem seu próprio caminho no P3).
 *
 * ⚠️ PRESENÇA NÃO É VALIDADE. Até 28/07 este filtro perguntava só "existe wrap com
 * este memberId?". Um membro cuja keypair foi INVALIDADA (reset de credencial → par
 * novo) continua listado com um wrap feito para a pública ANTIGA: ele não é
 * "faltante", é presente-e-inútil, e o self-heal NUNCA o reprovisionava — ficava cego
 * naquele caso para sempre, mesmo com leitores vivos de sobra.
 *
 * A comparação é por `keypairRef` (referência OPACA da keypair). Wrap cuja referência
 * difere da vigente é LIXO CRIPTOGRÁFICO e o membro volta a ser faltante.
 *
 * Por que NÃO apagamos o wrap morto (forma (i), rejeitada no portão de 28/07): apagar
 * daria ao admin uma ESCRITA dentro das linhas de denúncia — precedente de ação
 * administrativa que destrói material de caso, com raio total (apagar os wraps de
 * todos = ninguém lê nada, nunca). Com referência, a invalidação toca só o registro do
 * USUÁRIO; os wraps ficam onde estão e passam a ser reconhecidos como obsoletos. O
 * admin permanece fora do acervo (ADR-0002).
 */
export function findMissingMembers(
  publicKeys: ReadonlyArray<PublicKeyEntry>,
  existingWraps: ReadonlyArray<{ memberId: string; keypairRef?: string }> | null | undefined,
): PublicKeyEntry[] {
  // Referências VIGENTES já embrulhadas, por membro (um membro pode ter wrap velho
  // E novo — append-only: nada é sobrescrito).
  const refsPorMembro = new Map<string, Set<string>>();
  for (const w of existingWraps ?? []) {
    const set = refsPorMembro.get(w.memberId) ?? new Set<string>();
    set.add(refDe(w));
    refsPorMembro.set(w.memberId, set);
  }
  const out: PublicKeyEntry[] = [];
  const seen = new Set<string>();
  for (const pk of publicKeys) {
    if (pk.memberId === IA_ANALISTA_MEMBER_ID) continue; // humanos nesta entrega
    if (refsPorMembro.get(pk.memberId)?.has(refDe(pk)) === true) continue; // já LÊ
    if (seen.has(pk.memberId)) continue; // dedup defensivo
    if (typeof pk.publicKeyJwk !== 'string' || pk.publicKeyJwk.length === 0) continue;
    seen.add(pk.memberId);
    out.push(pk); // com a pública VIGENTE — reprovisionar com a antiga refaria o lixo
  }
  return out;
}

/**
 * CRIPTO — gera um wrap RSA da DEK para cada membro faltante. A DEK vem em claro do
 * escopo do withCaseDek do leitor (a lib NÃO carrega/decifra nada; recebe a DEK e a
 * usa efêmera). Um wrap que falhe (JWK inválido) é PULADO — não derruba os demais
 * (disponibilidade > completude, paridade ADR-0011). Retorna só ciphertext base64.
 */
export async function rewrapForMissingMembers(
  dek: Uint8Array,
  missing: ReadonlyArray<PublicKeyEntry>,
): Promise<MemberWrap[]> {
  const out: MemberWrap[] = [];
  for (const pk of missing) {
    try {
      const wrapped = await wrapDekForMember(dek, pk.publicKeyJwk);
      out.push({ memberId: pk.memberId, wrappedDek: bytesToBase64(wrapped) });
    } catch {
      // JWK do membro corrompido/incompatível — pula (o próximo leitor tenta de novo).
    }
  }
  return out;
}
